import { type ReactNode, useEffect, useRef } from 'react';
import { Loader2 } from '@alchemer/ui/icons';
import { tw } from '@/lib/tailwind';
import { ShareListItem } from './ShareListItem';
import type { ShareAccessLevel, ShareListItemData } from '@/types';

interface ShareListProps {
  isLoading: boolean;
  sharedItems: ShareListItemData[];
  availableItems: ShareListItemData[];
  searchResults: ShareListItemData[];
  searchSharedResults: ShareListItemData[];
  searchQuery: string;
  isSearchPending: boolean;
  onAdd: (item: ShareListItemData) => void;
  onRemove: (item: ShareListItemData) => void;
  onPermissionChange: (id: string, permission: ShareAccessLevel) => void;
  onShareDataChange: (id: string, shareData: boolean) => void;
}

const sectionLabelClass = tw(
  'sticky top-0 bg-gray-50 px-3 py-1 text-xs font-medium tracking-wide text-gray-400 uppercase',
);

function SectionLabel({ children, nodeRef }: { children: ReactNode; nodeRef?: React.RefObject<HTMLLIElement | null> }) {
  return (
    <li
      ref={nodeRef}
      role="separator"
      className={sectionLabelClass}
    >
      {children}
    </li>
  );
}

function ShareList({
  isLoading,
  sharedItems,
  availableItems,
  searchResults,
  searchSharedResults,
  searchQuery,
  onAdd,
  onRemove,
  onPermissionChange,
  onShareDataChange,
  isSearchPending,
}: ShareListProps) {
  const isSearching = searchQuery.trim().length > 0;
  const hasSearchResults = searchResults.length > 0;
  const hasSearchSharedResults = searchSharedResults.length > 0;
  const resultsRef = useRef<HTMLLIElement>(null);

  // Only scroll when results first arrive for a new query (false → true transition).
  const prevHasSearchResultsRef = useRef(false);
  useEffect(() => {
    if (hasSearchResults && !prevHasSearchResultsRef.current) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevHasSearchResultsRef.current = hasSearchResults;
  }, [hasSearchResults]);

  const totalResultCount = searchResults.length + searchSharedResults.length;

  if (isLoading) {
    return (
      <div className={tw('flex h-52 items-center justify-center rounded', 'border-border border')}>
        <Loader2 className={tw('h-6 w-6 animate-spin text-gray-400')} />
      </div>
    );
  }

  return (
    <>
      {/* Live region announces result count changes to screen readers */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className={tw('sr-only')}
      >
        {isSearching && !isSearchPending ? `${totalResultCount} result${totalResultCount === 1 ? '' : 's'} found` : ''}
      </div>

      <ul
        aria-label="Users"
        className={tw('border-border h-90 overflow-y-auto rounded border')}
      >
        {!isSearching && (
          <>
            <SectionLabel>Shared</SectionLabel>
            {sharedItems.length > 0 ? (
              sharedItems.map((item) => (
                <ShareListItem
                  key={item.id}
                  item={item}
                  variant="shared"
                  onRemove={onRemove}
                  onPermissionChange={onPermissionChange}
                  onShareDataChange={onShareDataChange}
                />
              ))
            ) : (
              <li className={tw('px-3 py-2 text-sm text-gray-400')}>No one has access yet.</li>
            )}

            <SectionLabel>Available</SectionLabel>
            {availableItems.length > 0 ? (
              availableItems.map((item) => (
                <ShareListItem
                  key={item.id}
                  item={item}
                  variant="available"
                  onAdd={onAdd}
                  onPermissionChange={onPermissionChange}
                  onShareDataChange={onShareDataChange}
                />
              ))
            ) : (
              <li className={tw('px-3 py-2 text-sm text-gray-400')}>No other users to add.</li>
            )}
          </>
        )}

        {isSearching && (
          <>
            {!hasSearchSharedResults && !hasSearchResults && !isSearchPending ? (
              <li className={tw('px-3 py-2 text-sm text-gray-400')}>No results for &ldquo;{searchQuery}&rdquo;</li>
            ) : (
              <>
                <SectionLabel>Shared</SectionLabel>
                {hasSearchSharedResults ? (
                  searchSharedResults.map((item) => (
                    <ShareListItem
                      key={item.id}
                      item={item}
                      variant="shared"
                      onRemove={onRemove}
                      onPermissionChange={onPermissionChange}
                      onShareDataChange={onShareDataChange}
                    />
                  ))
                ) : !isSearchPending ? (
                  <li className={tw('px-3 py-2 text-sm text-gray-400')}>No shared users match.</li>
                ) : null}

                <SectionLabel nodeRef={resultsRef}>Available</SectionLabel>
                {hasSearchResults ? (
                  searchResults.map((item) => (
                    <ShareListItem
                      key={item.id}
                      item={item}
                      variant="available"
                      onAdd={onAdd}
                      onPermissionChange={onPermissionChange}
                      onShareDataChange={onShareDataChange}
                    />
                  ))
                ) : !isSearchPending ? (
                  <li className={tw('px-3 py-2 text-sm text-gray-400')}>No results for &ldquo;{searchQuery}&rdquo;</li>
                ) : null}
              </>
            )}
          </>
        )}
      </ul>
    </>
  );
}

export { ShareList };
